/**
 * OpenClose Style01 - Stitch v1: glass + icon + name + slider (CLOSE ↔ OPEN)
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { TraitControlProps } from './types'
import { resolveRangeFromAttributes, getTraitConfig } from './traitStyleConfig'
import './OpenCloseStyle01.css'

export function OpenCloseStyle01({
  trait,
  device,
  state,
  attributes,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const { icon } = getTraitConfig(trait)
  const deviceName = device?.nickname ?? device?.name ?? device?.displayName ?? 'OpenClose'
  const { min: MIN, max: MAX } = useMemo(
    () => resolveRangeFromAttributes(attributes, trait, 'openClose'),
    [attributes, trait]
  )
  const raw = (state?.openPercent ?? state?.openClosePercent ?? state?.position) as number | undefined
  const current = typeof raw === 'number' ? Math.max(MIN, Math.min(MAX, Math.round(raw))) : Math.round((MIN + MAX) / 2)
  const [percent, setPercent] = useState(current)
  const lastSentRef = useRef<{ value: number; at: number } | null>(null)
  const stateToken = percent > MIN ? 'on open' : 'off close'

  useEffect(() => {
    const v = typeof raw === 'number' ? Math.max(MIN, Math.min(MAX, Math.round(raw))) : Math.round((MIN + MAX) / 2)
    setPercent(v)
  }, [raw, MIN, MAX])

  // 左 OPEN(100%) / 右 CLOSE(0%)，0%=滿(關)、100%=空(開)
  const sliderValue = MIN + MAX - percent  // 反轉：thumb 右=CLOSE=0% open
  const toOpenPercent = useCallback((sliderVal: number) => {
    const openPercent = MIN + MAX - sliderVal
    return Math.max(MIN, Math.min(MAX, Math.round(openPercent)))
  }, [MIN, MAX])

  const handlePreview = (sliderVal: number) => {
    const safe = toOpenPercent(sliderVal)
    setPercent(safe)
  }

  const handleCommit = (sliderVal: number) => {
    const safe = toOpenPercent(sliderVal)
    setPercent(safe)
    const now = Date.now()
    if (
      lastSentRef.current &&
      lastSentRef.current.value === safe &&
      now - lastSentRef.current.at < 250
    ) return
    lastSentRef.current = { value: safe, at: now }
    // Turing ExecuteCommand expects OpenClose + openPercent.
    void onCommand('openClose', { openPercent: safe })
  }

  return (
    <div className="trait-openclose-style01 trait-stitch-glass">
      <div className="trait-openclose-style01__header">
        <div className="trait-openclose-style01__title-row">
          <span
            className={`material-symbols-outlined trait-openclose-style01__icon`}
            data-css-slot="openCloseIcon"
            data-css-slot-label="OpenClose Icon"
            data-css-state={stateToken}
          >
            {icon}
          </span>
          <span
            className="trait-openclose-style01__title"
            data-css-slot="openCloseTitle"
            data-css-slot-label="OpenClose Title"
            data-css-state={stateToken}
          >
            {deviceName}
          </span>
        </div>
        <span
          className="trait-openclose-style01__value"
          data-css-slot="openCloseValue"
          data-css-slot-label="OpenClose Value"
          data-css-state={stateToken}
        >
          {percent}%
        </span>
      </div>
      <div
        className="trait-openclose-style01__slider-wrap"
        data-css-slot="openCloseControl"
        data-css-slot-label="OpenClose Control"
        data-css-state={stateToken}
      >
        <div
          className="trait-openclose-style01__slider-fill"
          style={{ '--fill-percent': `${((MAX - percent + MIN) / (MAX - MIN || 1)) * 100}%` } as React.CSSProperties}
        />
        <input
          type="range"
          min={MIN}
          max={MAX}
          value={sliderValue}
          onChange={(e) => handlePreview(Number((e.target as HTMLInputElement).value))}
          onPointerUp={(e) => handleCommit(Number((e.target as HTMLInputElement).value))}
          onMouseUp={(e) => handleCommit(Number((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => handleCommit(Number((e.target as HTMLInputElement).value))}
          className="trait-openclose-style01__slider"
          disabled={disabled}
        />
        <div
          className="trait-openclose-style01__labels"
          aria-hidden
          data-css-slot="openCloseLabels"
          data-css-slot-label="OpenClose Labels"
          data-css-state={stateToken}
        >
          <span>OPEN</span>
          <span>CLOSE</span>
        </div>
      </div>
    </div>
  )
}
